/**
 * @file types.ts
 *
 * Core types for the shop admin API seam. These mirror the design-reviewed
 * Website Builder v2 response envelope so the ported screens and stores can be
 * copied near-verbatim. The envelope (data + pagination totals) and the typed
 * error are backend-agnostic: today they are served by MSW mocks; when the
 * custom commerce API lands, a mapping adapter in this folder will produce the
 * same shapes and the screens stay untouched.
 */

/**
 * Paginated response envelope. Totals come from the `X-Total-Count` /
 * `X-Total-Pages` headers (kept for parity with the reference contract).
 *
 * @template T - The type of the `data` payload.
 */
export interface WcResult<T> {
  data: T;
  /** Total number of matching records across all pages (`X-Total-Count`). */
  total: number;
  /** Total number of pages (`X-Total-Pages`). */
  totalPages: number;
}

/**
 * Error thrown when the shop API responds with a non-OK HTTP status. Carries
 * the HTTP `status` and a machine-readable `code` from the error envelope so
 * callers can map failures without parsing message text.
 */
export class WcApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
  ) {
    super(message);
    this.name = 'WcApiError';
  }
}
